import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { mfaConfirmSetup, mfaSetupDetails, MfaSetupDetails } from 'config/axiosInstance';
import CodeField from './CodeField';
import { ErrorBox, MfaFrame, MfaStepProps, SubmitButton, useAttempt, useStepFailure } from './MfaFrame';
import { classifyMfaError } from './mfaErrors';
import { useCountdown } from './useCountdown';

/** 登録の情報の読み込みに失敗したときの文（login の名前空間の鍵と、待つ秒数）。 */
type LoadFailure = { message: string; seconds?: number };

/** キーを読みやすく4文字ずつに区切る（コピーするのは区切らない値）。 */
function groupKey(secret: string): string {
  return secret.replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * 認証アプリの登録（mfa が setup_required）。QR・手で入れる用のキー・手順を出し、最初のコードで確定する。
 *
 * QR はサーバーが作る SVG の data URI（data:image/svg+xml）を img の src にだけ入れる。HTML として
 * 埋め込まない（dangerouslySetInnerHTML を使わない）。秘密（キー）はこの画面の state だけに置き、
 * 保存・記録しない。
 */
const TotpSetupStep: React.FC<MfaStepProps> = ({ challenge, onRestart, onSignedIn }) => {
  const { t } = useTranslation('login');
  const [details, setDetails] = useState<MfaSetupDetails | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [copyNote, setCopyNote] = useState('');
  const failure = useStepFailure(onRestart);
  const beginConfirm = useAttempt();
  // #385 の Codex の指摘 4368093970。読み込み（QR・キー）が時間切れ・通信の失敗・429 で失敗したら、
  // 理由と「もう一度読み込む」を出す。429 は Retry-After の秒数が過ぎるまで押せない。
  const [loadFailure, setLoadFailure] = useState<LoadFailure | null>(null);
  const [loadRound, setLoadRound] = useState(0);
  const [loadWait, startLoadWait] = useCountdown();
  const restartRef = useRef(onRestart);
  restartRef.current = onRestart;

  useEffect(() => {
    const controller = new AbortController();
    setLoadFailure(null);
    mfaSetupDetails(challenge.token, { signal: controller.signal })
      .then((value) => {
        if (!controller.signal.aborted) setDetails(value);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        const failed = classifyMfaError(error);
        switch (failed.kind) {
          case 'aborted':
            return;
          case 'restart':
            // チャレンジが使えない（401・409。期限切れなど）・2段階認証を使えない（503）は、読み直しても
            // 通らないので、パスワードの画面へ戻す。それ以外（500 などの一時的な失敗・形の違う応答）は読み直せる。
            if ([401, 409, 503].includes((error as any)?.response?.status)) {
              restartRef.current(failed.message);
            } else {
              setLoadFailure({ message: 'mfa.setup-load-failed' });
            }
            return;
          case 'throttled':
            if (failed.seconds) startLoadWait(failed.seconds);
            setLoadFailure({ message: 'mfa.setup-load-throttled', seconds: failed.seconds ?? undefined });
            return;
          case 'network':
            setLoadFailure({ message: 'error_network' });
            return;
          case 'origin_not_allowed':
            setLoadFailure({ message: 'error_origin_not_allowed' });
            return;
          default:
            setLoadFailure({ message: 'mfa.setup-load-failed' });
        }
      });
    return () => controller.abort();
  }, [challenge.token, loadRound, startLoadWait]);

  const copy = async () => {
    if (!details) return;
    try {
      await navigator.clipboard.writeText(details.secret);
      setCopyNote(t('mfa.setup-copied'));
    } catch {
      setCopyNote(t('mfa.setup-copy-failed'));
    }
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      failure.setText(t('mfa.error-code-format'));
      return;
    }
    setBusy(true);
    failure.setText('');
    const signal = beginConfirm();
    try {
      const outcome = await mfaConfirmSetup(challenge.token, code, { signal });
      if (!signal.aborted) onSignedIn(outcome);
    } catch (error) {
      if (signal.aborted) return; // やめた試行の失敗は、今の画面に当てない
      failure.handle(error);
      setBusy(false);
    }
  };

  const errorId = failure.text ? 'mfa-setup-error' : undefined;
  return (
    <MfaFrame
      title={t('mfa.setup-title')}
      subtitle={t('mfa.setup-subtitle')}
      onBack={() => onRestart(null)}
      backDisabled={busy}
    >
      <ol className="list-decimal pl-6 flex flex-col gap-2 text-body-2 text-neutral/300">
        <li>{t('mfa.setup-step1')}</li>
        <li>{t('mfa.setup-step2')}</li>
        <li>{t('mfa.setup-step3')}</li>
      </ol>
      {details ? (
        <div className="flex flex-wrap items-center gap-6">
          <img
            src={details.qrSvg}
            alt={t('mfa.setup-qr-alt')}
            width={176}
            height={176}
            className="w-44 h-44 border border-neutral/50 rounded-md bg-white p-2"
          />
          <div className="flex flex-col gap-2 min-w-0">
            <p id="mfa-setup-key-label" className="text-sm text-neutral/100">
              {t('mfa.setup-key')}
            </p>
            <code
              aria-labelledby="mfa-setup-key-label"
              data-testid="mfa-setup-key"
              className="font-mono text-body-1 text-neutral/300 break-all select-all"
            >
              {groupKey(details.secret)}
            </code>
            <button
              type="button"
              className="self-start px-4 py-2 rounded-md border border-primary/300 text-primary/300"
              onClick={copy}
            >
              {t('mfa.setup-copy')}
            </button>
            <p role="status" className="text-body-2 text-neutral/300">
              {copyNote}
            </p>
          </div>
        </div>
      ) : loadFailure ? (
        <div className="flex flex-col items-start gap-3">
          <ErrorBox
            id="mfa-setup-load-error"
            text={
              loadFailure.message === 'mfa.setup-load-throttled'
                ? loadWait > 0
                  ? t('mfa.error-throttled-seconds', { seconds: loadWait })
                  : t('error_throttled')
                : t(loadFailure.message)
            }
          />
          <button
            type="button"
            className={`px-4 py-2 rounded-md border ${
              loadWait > 0 ? 'border-neutral/50 text-neutral/300 cursor-not-allowed' : 'border-primary/300 text-primary/300'
            }`}
            disabled={loadWait > 0}
            onClick={() => setLoadRound((n) => n + 1)}
          >
            {loadWait > 0 ? t('mfa.setup-retry-wait', { seconds: loadWait }) : t('mfa.setup-retry')}
          </button>
        </div>
      ) : (
        <p role="status" aria-busy="true" className="text-body-2 text-neutral/300">
          {t('mfa.setup-loading')}
        </p>
      )}
      <form onSubmit={submit} className="flex flex-col gap-6" noValidate>
        <CodeField
          id="mfa-setup-code"
          value={code}
          errorId={errorId}
          disabled={!details}
          onChange={(value) => {
            setCode(value);
            failure.setText('');
          }}
        />
        <ErrorBox id="mfa-setup-error" text={failure.text} />
        <SubmitButton busy={busy} disabled={!details || failure.waiting} label={t('mfa.setup-button')} />
      </form>
    </MfaFrame>
  );
};

export default TotpSetupStep;
